import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTypeaheadDatesAndOccupancy } from 'selectors/typeaheadSelectors'
import {
  convertDateRangesToMonthString,
  getStayFromCheckInWindow,
  mapFlexibleNightsToDurationRange,
} from 'lib/search/flexibleDateUtils'
import { mapExtractedHotelNightsToDuration } from 'lib/search/hotelFlexibleDuration'

interface UseExtractDatesAndOccupancyResult {
  extractedDates: App.ExtractDatesResult | undefined
  extractedOccupancy: App.ExtractOccupancyResult | undefined
}

const EmptyDatesAndOccupancy = { travelDates: undefined, occupancy: undefined }

export const useExtractDatesAndOccupancy = (
  typeaheadId: string | undefined,
  useExactHotelDurations: boolean = false,
): UseExtractDatesAndOccupancyResult => {
  const { travelDates, occupancy } = useAppSelector((state) =>
    typeaheadId
      ? getTypeaheadDatesAndOccupancy(state, typeaheadId)
      : EmptyDatesAndOccupancy,
  )

  const extractedDates = useMemo((): App.ExtractDatesResult | undefined => {
    if (!travelDates) return undefined

    if (travelDates.type === 'specific') {
      return {
        checkinDate: travelDates.checkIn,
        checkoutDate: travelDates.checkOut,
        flexibleMonths: undefined,
        flexibleNights: undefined,
      }
    }

    if (travelDates.type === 'flexible') {
      const stay = getStayFromCheckInWindow(travelDates)
      if (stay) {
        return {
          checkinDate: stay.checkIn,
          checkoutDate: stay.checkOut,
          flexibleMonths: undefined,
          flexibleNights: undefined,
        }
      }

      return {
        checkinDate: undefined,
        checkoutDate: undefined,
        flexibleNights: useExactHotelDurations
          ? mapExtractedHotelNightsToDuration(travelDates.numberOfNights)
          : mapFlexibleNightsToDurationRange(travelDates.numberOfNights),
        flexibleMonths: convertDateRangesToMonthString(travelDates.dateRanges),
      }
    }

    return undefined
  }, [useExactHotelDurations, travelDates])

  const extractedOccupancy = useMemo((): App.ExtractOccupancyResult | undefined => {
    if (!occupancy || occupancy.length === 0) return undefined

    return {
      occupancies: occupancy.map(room => ({
        adults: room.adults,
        children: room.childrenAges.length,
        childrenAge: room.childrenAges,
      })),
    }
  }, [occupancy])

  return {
    extractedDates,
    extractedOccupancy,
  }
}
